// src/components/ProviderConversationsScreen.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import supabaseService from '../services/supabaseService';
import { FiHome, FiList, FiTool, FiMessageSquare, FiUser, FiArrowRight, FiMenu, FiLogOut, FiCheck } from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import { formatTime } from '../utils/helpers';

// 🆕 معرّف حساب الدعم الخاص بالمزود — لإظهار شارة التوثيق
const PROVIDER_SUPPORT_CLIENT_ID = 'e9260ad4-663f-4f35-83ed-684aaf7ba34b';

const ProviderConversationsScreen = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [provider, setProvider] = useState(null);

  const isActive = (path) => location.pathname === path;

  const tabs = [
    { id: 0, name: 'الرئيسية', icon: FiHome, path: '/provider' },
    { id: 1, name: 'الطلبات', icon: FiList, path: '/provider' },
    { id: 2, name: 'الخدمات', icon: FiTool, path: '/provider' },
    { id: 3, name: 'الدردشة', icon: FiMessageSquare, path: '/provider/conversations' },
    { id: 4, name: 'الملف الشخصي', icon: FiUser, path: '/provider/profile' }
  ];

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 3000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      const [providerData, conversationsData] = await Promise.all([
        supabaseService.getProviderById(user.id),
        supabaseService.getConversationsForUser(user.id, { isClient: false })
      ]);

      setProvider(providerData);
      setConversations(conversationsData || []);
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/auth');
    } catch (error) {
      console.error("Error signing out:", error);
      toast.error('فشل تسجيل الخروج');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <>
      {/* تصميم الهاتف */}
      <div className="md:hidden h-screen w-full bg-gray-100 flex items-center justify-center overflow-hidden">
        <div className="max-w-md w-full h-full bg-white flex flex-col relative shadow-2xl">
          {/* Header */}
          <div className="bg-gradient-to-r from-gray-900 to-black p-4 text-white shadow-lg flex items-center justify-between">
            <Link
              to="/provider"
              className="p-2 rounded-full hover:bg-white/10 transition-colors"
            >
              <FiArrowRight className="text-xl" />
            </Link>
            <h1 className="text-lg font-bold">الدردشة</h1>
            <div className="w-10"></div>
          </div>

          <div className="flex-1 overflow-y-auto pb-20 scroll-smooth">
            <div className="p-4">
              {conversations.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="bg-gray-100 rounded-full p-6 mb-4">
                    <FiMessageSquare className="text-3xl text-gray-400" />
                  </div>
                  <p className="text-lg font-medium text-gray-600">لا توجد محادثات</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {conversations.map((conv) => {
                    // 🆕 التحقق مما إذا كانت هذه محادثة حساب الدعم
                    const isSupportAccount =
                      conv.clientId === PROVIDER_SUPPORT_CLIENT_ID ||
                      conv.client?.id === PROVIDER_SUPPORT_CLIENT_ID;

                    return (
                    <button
                      key={conv.id}
                      onClick={() => navigate(`/chat/${conv.id}`, {
                        state: {
                          otherUserName: conv.client?.fullName || 'عميل',
                          otherUserAvatarUrl: conv.client?.avatarUrl
                        }
                      })}
                      className="w-full bg-white rounded-lg p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow text-right"
                    >
                      <div className="flex items-center space-x-3 space-x-reverse">
                        <img
                          src={conv.client?.avatarUrl || 'https://via.placeholder.com/150'}
                          alt="Client"
                          className="w-12 h-12 rounded-full"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <p className="font-medium text-sm truncate">{conv.client?.fullName || 'عميل'}</p>
                            {isSupportAccount && (
                              <span
                                className="inline-flex items-center justify-center w-4 h-4 bg-blue-500 rounded-full flex-shrink-0"
                                title="حساب موثّق"
                              >
                                <FiCheck className="text-white" style={{ fontSize: '10px' }} />
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 truncate">
                            {conv.lastMessage?.content || 'الرسائل'}
                          </p>
                        </div>
                        <p className="text-xs text-gray-400 flex-shrink-0">
                          {conv.lastMessage?.createdAt ? formatTime(conv.lastMessage.createdAt) : ''}
                        </p>
                      </div>
                    </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Bottom Navigation */}
          <div className="bg-white border-t border-gray-900 w-full shrink-0 h-16 fixed bottom-0 left-0 right-0 z-50">
            <div className="flex h-full">
              {tabs.map((tab) => {
  const tabPath = tab.id <= 2 ? `/provider?tab=${tab.id}` : tab.path;
  return (
    <Link
      key={tab.id}
      to={tabPath}
      className={`flex-1 flex flex-col items-center justify-center transition-colors ${
        isActive(tab.path)
          ? 'text-gray-900'
          : 'text-gray-600 hover:text-gray-900'
      }`}
    >
      <tab.icon className="text-xl mb-1" />
      <span className="text-xs">{tab.name}</span>
    </Link>
  );
})}
            </div>
          </div>
        </div>
      </div>

      {/* تصميم الحاسوب */}
      <div className="hidden md:flex h-screen bg-gray-50 overflow-hidden">
        {/* Sidebar */}
        <div className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-gray-900 text-white transition-all duration-300 flex flex-col h-full`}>
          <div className="p-3 border-b border-gray-700 flex-shrink-0">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="w-full flex items-center justify-center md:justify-start space-x-2 space-x-reverse hover:bg-gray-800 p-2 rounded-lg transition-colors"
            >
              <FiMenu className="text-xl" />
              {sidebarOpen && <span className="font-medium">القائمة</span>}
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-2">
            {tabs.map((tab) => {
  const tabPath = tab.id <= 2 ? `/provider?tab=${tab.id}` : tab.path;
  return (
    <Link
      key={tab.id}
      to={tabPath}
      className={`w-full flex items-center space-x-3 space-x-reverse p-2.5 rounded-lg mb-1 transition-colors ${
        isActive(tab.path)
          ? 'bg-gray-800 text-white'
          : 'text-gray-300 hover:bg-gray-800'
      }`}
    >
      <tab.icon className="text-lg flex-shrink-0" />
      {sidebarOpen && <span className="text-sm font-medium">{tab.name}</span>}
    </Link>
  );
})}
          </div>

          {sidebarOpen && (
            <div className="p-3 border-t border-gray-700 flex-shrink-0 space-y-2">
              <div className="flex items-center space-x-3 space-x-reverse">
                <img
                  src={provider?.avatarUrl || 'https://via.placeholder.com/150'}
                  alt="Profile"
                  className="w-9 h-9 rounded-full"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{provider?.fullName}</p>
                  <p className="text-xs text-gray-400 truncate">{provider?.email}</p>
                </div>
              </div>
              
              <button
                onClick={handleSignOut}
                className="w-full flex items-center space-x-2 space-x-reverse p-2 rounded-lg hover:bg-red-700 bg-red-600 transition-colors text-sm"
              >
                <FiLogOut className="text-base" />
                <span>تسجيل الخروج</span>
              </button>
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden h-full">
          <div className="bg-white border-b border-gray-200 p-3 flex items-center justify-between flex-shrink-0">
            <h1 className="text-xl font-bold text-gray-900">الدردشة</h1>
          </div>

          <div className="flex-1 overflow-y-auto">
            <div className="max-w-4xl mx-auto p-5">
              {conversations.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <div className="bg-gray-100 rounded-full p-8 mb-4">
                    <FiMessageSquare className="text-4xl text-gray-400" />
                  </div>
                  <p className="text-xl font-medium text-gray-600">لا توجد محادثات</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {conversations.map((conv) => {
                    // 🆕 التحقق مما إذا كانت هذه محادثة حساب الدعم
                    const isSupportAccount =
                      conv.clientId === PROVIDER_SUPPORT_CLIENT_ID ||
                      conv.client?.id === PROVIDER_SUPPORT_CLIENT_ID;

                    return (
                    <button
                      key={conv.id}
                      onClick={() => navigate(`/chat/${conv.id}`, {
                        state: {
                          otherUserName: conv.client?.fullName || 'عميل',
                          otherUserAvatarUrl: conv.client?.avatarUrl
                        }
                      })}
                      className="w-full bg-gray-50 rounded-xl p-5 shadow-sm border border-gray-200 hover:shadow-md transition-shadow text-right"
                    >
                      <div className="flex items-center space-x-4 space-x-reverse">
                        <img
                          src={conv.client?.avatarUrl || 'https://via.placeholder.com/150'}
                          alt="Client"
                          className="w-14 h-14 rounded-full"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <p className="font-medium text-base truncate">{conv.client?.fullName || 'عميل'}</p>
                            {isSupportAccount && (
                              <span
                                className="inline-flex items-center justify-center w-4 h-4 bg-blue-500 rounded-full flex-shrink-0"
                                title="حساب موثّق"
                              >
                                <FiCheck className="text-white" style={{ fontSize: '10px' }} />
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 truncate">
                            {conv.lastMessage?.content || '  الرسائل'}
                          </p>
                        </div>
                        <p className="text-sm text-gray-400 flex-shrink-0">
                          {conv.lastMessage?.createdAt ? formatTime(conv.lastMessage.createdAt) : ''}
                        </p>
                      </div>
                    </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProviderConversationsScreen;